import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import type { TopSkill } from '../../api/types';

interface Props {
  skills: TopSkill[];
  loading?: boolean;
}

function formatNumber(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return String(value);
}

export function TopSkillsBarChart({ skills, loading }: Props): JSX.Element {
  // Sort ascending so the largest bar lands at the top of a horizontal chart.
  const sorted = [...skills].sort((a, b) => a.calls - b.calls);

  const option: EChartsOption = {
    grid: { left: 168, right: 80, top: 16, bottom: 56 },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const items = Array.isArray(params) ? params : [params];
        const first = items[0];
        if (!first) return '';
        const idx = first.dataIndex as number;
        const entry = sorted[idx];
        if (!entry) return '';
        return [
          `<strong>${entry.skill}</strong>`,
          `Loads: ${entry.calls.toLocaleString('en-US')}`,
          `Sessions: ${entry.sessions.toLocaleString('en-US')}`,
          `Users: ${entry.users.toLocaleString('en-US')}`,
        ].join('<br/>');
      },
    },
    xAxis: {
      type: 'value',
      name: 'Skill loads',
      nameLocation: 'middle',
      nameGap: 24,
      axisLabel: { formatter: (v: number) => formatNumber(v) },
    },
    yAxis: {
      type: 'category',
      data: sorted.map((s) => s.skill),
      axisLabel: { fontSize: 12 },
    },
    series: [
      {
        type: 'bar',
        barMaxWidth: 18,
        itemStyle: { color: '#10b981', borderRadius: [0, 4, 4, 0] },
        data: sorted.map((s) => s.calls),
        label: {
          show: true,
          position: 'right',
          formatter: (p) => formatNumber(p.value as number),
          fontSize: 11,
          color: 'inherit',
        },
      },
    ],
  };

  return (
    <ReactECharts
      option={option}
      style={{ height: Math.max(304, sorted.length * 32 + 88), width: '100%' }}
      showLoading={loading}
      notMerge
      lazyUpdate
    />
  );
}
